import { useState } from 'react';
import { AreaPopulationDetails, ParcelDetails } from './AtlasIntelligenceDetails';
import { ArrowUpRight, Building2, FileText, MapPin, Scale, ShieldAlert, X } from 'lucide-react';
import { Link } from 'react-router';
import { Button, Spinner } from '@helios/ui';
import type { UseQueryResult } from '@tanstack/react-query';
import type {
  IntelligenceSource,
  LocationIntelligence,
  LocationMetric,
  ResourceLayer,
} from './atlasApi';
import { jobHref, money, number, stageLabel, type Job, type MapPoint } from './atlasModel';
export function SourceStatus({ source }: { source: IntelligenceSource }) {
  return (
    <div className="atlas-source">
      <div>
        <strong>{source.name}</strong>
        <span data-state={source.state}>{source.state.replaceAll('_', ' ')}</span>
      </div>
      <p>{source.detail}</p>
      {source.url ? (
        <a href={source.url} target="_blank" rel="noreferrer">
          View source ↗
        </a>
      ) : null}
    </div>
  );
}
function Metric({ metric }: { metric: LocationMetric }) {
  return (
    <div>
      <dt>{metric.label}</dt>
      <dd>{metric.unit.startsWith('USD') ? money(metric.value) : number(metric.value)}</dd>
      <small>
        {metric.margin !== null ? '± ' + number(metric.margin) + ' · ' : ''}
        {metric.unit}
      </small>
    </div>
  );
}
export function AtlasInspector({
  job,
  point,
  radius,
  onRadius,
  query,
  nearby,
  resourceData,
  onClose,
  onSources,
  onBrief,
  onCompare,
  compareCount,
}: {
  job: Job | null;
  point: MapPoint | null;
  radius: number;
  onRadius: (value: number) => void;
  query: Pick<
    UseQueryResult<LocationIntelligence>,
    'data' | 'isFetching' | 'isError' | 'error' | 'refetch'
  >;
  nearby: Job[];
  resourceData?: ResourceLayer;
  onClose: () => void;
  onSources: () => void;
  onBrief: () => void;
  onCompare: () => void;
  compareCount: number;
}) {
  const [tab, setTab] = useState('overview');
  const data = query.data;
  const knownClients = new Set(
    nearby.flatMap((j) => (j.canonicalClientIdentityId ? [j.canonicalClientIdentityId] : [])),
  );
  const resources = job
    ? resourceData?.projects.find((p) => p.projectId === job.sourceId)
    : undefined;
  if (!job && !point && !query.isFetching && !query.isError && !data)
    return (
      <aside className="atlas-inspector atlas-inspector-empty" aria-label="Location intelligence">
        <MapPin size={26} />
        <h2>Location intelligence</h2>
        <p>Select a job, click a point on the map, or look up a full US street address.</p>
        <div>
          <strong>Understand the area</strong>
          <p>
            Demographics, business activity, site conditions and your nearby work, with sources and
            coverage.
          </p>
        </div>
        <button type="button" onClick={onSources} className="atlas-text-button">
          Sources & coverage →
        </button>
      </aside>
    );
  return (
    <aside className="atlas-inspector" aria-label="Location intelligence">
      <div className="atlas-panel-heading">
        <h2>
          <MapPin size={15} />
          Location intelligence
        </h2>
        <button type="button" aria-label="Close location intelligence" onClick={onClose}>
          <X size={15} />
        </button>
      </div>
      <div className="atlas-place-heading">
        <h3>{job?.name ?? data?.location?.label ?? point?.label ?? 'Address lookup'}</h3>
        <p>
          {job
            ? [job.location.city, job.location.state].filter(Boolean).join(', ') ||
              'Location not recorded'
            : data?.location?.label}
        </p>
        {job ? (
          <div>
            <span className="atlas-stage">{stageLabel(job.cohort)}</span>
            <Link to={jobHref(job)}>
              Open record <ArrowUpRight size={12} />
            </Link>
          </div>
        ) : null}
      </div>
      <div className="atlas-inspector-tabs" role="tablist" aria-label="Location detail">
        {['overview', 'people', 'businesses', 'site'].map((value) => (
          <button
            type="button"
            role="tab"
            id={'atlas-tab-' + value}
            aria-controls="atlas-location-panel"
            key={value}
            aria-selected={tab === value}
            onClick={() => setTab(value)}
          >
            {value}
          </button>
        ))}
      </div>
      <div className="atlas-radius" aria-label="Analysis radius">
        {[1, 3, 5].map((value) => (
          <button
            type="button"
            key={value}
            aria-pressed={radius === value}
            onClick={() => onRadius(value)}
          >
            {value} {value === 1 ? 'mile' : 'miles'}
          </button>
        ))}
      </div>
      <div
        className="atlas-inspector-scroll"
        id="atlas-location-panel"
        role="tabpanel"
        aria-labelledby={'atlas-tab-' + tab}
      >
        {query.isFetching ? (
          <div className="atlas-source-loading" role="status">
            <Spinner className="h-4 w-4" />
            Reading location sources…
          </div>
        ) : null}
        {query.isError ? (
          <div className="atlas-notice" role="alert">
            <p>{query.error instanceof Error ? query.error.message : 'Location lookup failed.'}</p>
            <Button size="sm" variant="outline" onClick={() => void query.refetch()}>
              Retry location
            </Button>
          </div>
        ) : null}
        {data ? (
          <button
            type="button"
            className="atlas-text-button"
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Refresh location sources
          </button>
        ) : null}
        {!point && job ? (
          <div className="atlas-notice">
            This record has no verified coordinates. Open its source record to review the location,
            or research an address above.
          </div>
        ) : null}
        {tab === 'overview' || tab === 'people' ? (
          <section>
            <h4>
              Area overview <span>Census tract</span>
            </h4>
            <dl className="atlas-metrics">
              {(data?.demographics.metrics ?? [])
                .slice(0, tab === 'people' ? 8 : 4)
                .map((metric) => (
                  <Metric key={metric.id} metric={metric} />
                ))}
            </dl>
            {!data ? (
              <p className="atlas-muted">No demographic observations loaded.</p>
            ) : (
              <p className="atlas-provenance">
                {data.demographics.geography ?? 'Geography unavailable'}
                <br />
                {data.demographics.period}
                <br />
                Whole-tract estimates, not radius totals. ± is the 90% margin of error.
              </p>
            )}
            {data?.sources
              .filter((source) => source.id === 'acs' && source.state !== 'available')
              .map((source) => <SourceStatus key={source.id} source={source} />)}
          </section>
        ) : null}
        {tab === 'people' ? (
          <>
            <AreaPopulationDetails data={data} />
            {data?.sources
              .filter((s) => ['growth', 'daytime'].includes(s.id))
              .map((s) => <SourceStatus key={s.id} source={s} />)}
          </>
        ) : null}
        {tab === 'overview' || tab === 'businesses' ? (
          <section>
            <h4>
              <Building2 size={13} />
              Business activity <span>County</span>
            </h4>
            {data?.businesses.industries.length ? (
              <>
                <div className="atlas-industry-head">
                  <span>Industry</span>
                  <span>Locations / jobs</span>
                </div>
                {data.businesses.industries
                  .filter((i) => tab === 'businesses' || i.code !== '00')
                  .slice(0, tab === 'businesses' ? 10 : 4)
                  .map((industry) => {
                    const total = data.businesses.industries.find(
                      (i) => i.code === '00',
                    )?.establishments;
                    const percentage =
                      total && industry.establishments !== null
                        ? Math.min(100, (industry.establishments / total) * 100)
                        : null;
                    return (
                      <div key={industry.code} className="atlas-industry">
                        <div>
                          <span>{industry.label}</span>
                          <strong>
                            {number(industry.establishments)}{' '}
                            <small>/ {number(industry.employees)}</small>
                          </strong>
                        </div>
                        {percentage !== null ? (
                          <div className="atlas-bar">
                            <i style={{ width: percentage + '%' }} />
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
              </>
            ) : (
              <p className="atlas-muted">No county business observations available.</p>
            )}
            <p className="atlas-provenance">
              {data?.businesses.geography}
              <br />
              {data?.businesses.period ?? 'County Business Patterns'} · Employer establishments /
              employment. Not nearby place counts.
            </p>
            {data?.sources
              .filter((source) => source.id === 'cbp' && source.state !== 'available')
              .map((source) => <SourceStatus key={source.id} source={source} />)}
            {tab === 'businesses' ? (
              <p className="atlas-provenance">
                Enable Suppliers or Nearby amenities to inspect community-mapped places on the map.
                Coverage is partial and may omit businesses.
              </p>
            ) : null}
          </section>
        ) : null}
        {tab === 'overview' || tab === 'site' ? (
          <section>
            <h4>
              <ShieldAlert size={13} />
              Site & access
            </h4>
            <dl className="atlas-facts">
              <div>
                <dt>Flood screening</dt>
                <dd>
                  {data?.flood.zones.length
                    ? data.flood.zones.map((z) => z.zone).join(', ')
                    : 'Coverage unverified'}
                </dd>
              </div>
              <div>
                <dt>Location precision</dt>
                <dd>
                  {data?.location?.precision === 'address_range'
                    ? 'Street range'
                    : point
                      ? 'Recorded / selected point'
                      : 'Unlocated'}
                </dd>
              </div>
              <div>
                <dt>Tract / county FIPS</dt>
                <dd>
                  {data?.location?.tract ?? 'Unavailable'} / {data?.location?.countyFips ?? '—'}
                </dd>
              </div>
              <div>
                <dt>Zoning & ownership</dt>
                <dd>
                  {data?.parcels?.records.length
                    ? data.parcels.records.length + ' parcel record(s)'
                    : 'Unavailable'}
                </dd>
              </div>
            </dl>
            <p className="atlas-provenance">
              {data?.flood.coverage ?? 'No location lookup yet.'} A missing flood polygon does not
              mean no flood risk.
            </p>
            {tab === 'site' ? (
              <>
                {data?.sources
                  .filter((s) =>
                    ['geocoder', 'tiger-geography', 'fema', 'parcels', 'places'].includes(s.id),
                  )
                  .map((s) => <SourceStatus key={s.id} source={s} />)}
                <p className="atlas-provenance">
                  Road access shows mapped road features. Travel time, live congestion, parcel
                  verification and permit approval require jurisdiction review.
                </p>
              </>
            ) : null}
          </section>
        ) : null}
        {tab === 'site' ? <ParcelDetails data={data} /> : null}
        <section>
          <h4>
            Your footprint <span>Within {radius} mi</span>
          </h4>
          <dl className="atlas-footprint">
            <div>
              <dt>Located jobs</dt>
              <dd>{point ? nearby.length : '—'}</dd>
            </div>
            <div>
              <dt>Known clients</dt>
              <dd>{point ? knownClients.size : '—'}</dd>
            </div>
          </dl>
          <p className="atlas-provenance">
            Straight-line distance · current filters · unlocated records excluded. Client count uses
            linked client identities only.
          </p>
        </section>
        {job ? (
          <section>
            <h4>Job record</h4>
            <dl className="atlas-facts">
              <div>
                <dt>Contract value</dt>
                <dd>{money(job.financials?.contractValue)}</dd>
              </div>
              <div>
                <dt>Recorded risk</dt>
                <dd>{job.riskLevel ?? 'Unrecorded'}</dd>
              </div>
              <div>
                <dt>Source status</dt>
                <dd>{job.status || 'Unrecorded'}</dd>
              </div>
              <div>
                <dt>Start date</dt>
                <dd>{job.schedule.startDate?.slice(0, 10) ?? 'Unrecorded'}</dd>
              </div>
              <div>
                <dt>Source</dt>
                <dd>
                  {job.source} · {job.projectNumber}
                </dd>
              </div>
              {resourceData && job.source === 'project' ? (
                <>
                  <div>
                    <dt>Assigned crew members</dt>
                    <dd>
                      {resourceData.crews === 'restricted'
                        ? 'Restricted'
                        : (resources?.assignedHeadcount ?? 0)}
                    </dd>
                  </div>
                  <div>
                    <dt>Equipment assignments</dt>
                    <dd>
                      {resourceData.equipment === 'restricted'
                        ? 'Restricted'
                        : (resources?.equipmentCount ?? 0)}
                    </dd>
                  </div>
                </>
              ) : null}
            </dl>
            {resourceData ? (
              <p className="atlas-provenance">
                Current recorded assignments at the job site, not live personnel or equipment
                positions.
              </p>
            ) : null}
          </section>
        ) : null}
      </div>
      <footer className="atlas-inspector-footer">
        <button type="button" className="atlas-text-button" onClick={onSources}>
          Sources & coverage ↗
        </button>
        <div>
          <Button size="sm" disabled={!point || query.isFetching} onClick={onBrief}>
            <FileText size={14} />
            Open brief
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!point || query.isFetching}
            onClick={onCompare}
          >
            <Scale size={14} />
            Compare{compareCount ? ' (' + compareCount + ')' : ''}
          </Button>
        </div>
      </footer>
    </aside>
  );
}
